"use client";

import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapPin } from "@fortawesome/pro-solid-svg-icons/faMapPin";
import { faHeart } from "@fortawesome/pro-solid-svg-icons/faHeart";
import { faPersonWalking } from "@fortawesome/pro-solid-svg-icons/faPersonWalking";
import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { faHeart as faHeartRegular } from "@fortawesome/pro-regular-svg-icons/faHeart";
import { getTranslatedValue } from "@wondermaps/db/client";
import { type POI } from "../MapCanvas";
import { findIconByName } from "../../data/fontawesome-icons";
import { formatWalkTime } from "../../lib/walk-time";
import { cn } from "@wondermaps/ui";
import { lightenHex } from "../../lib/color-utils";
import { getCloudinaryImageUrl } from "../ui/CloudinaryImage";

/** Legacy sponsor tier badge colors (backward compat until Phase 5 deprecation) */
const LEGACY_SPONSOR_COLORS: Record<string, string> = {
  gold: "#FFD700",
  silver: "#A8A9AD",
  bronze: "#CD7F32",
};

const FALLBACK_CATEGORY_COLOR = "#888888";

export interface POIListItemProps {
  poi: POI;
  distance?: number;
  onClick: () => void;
  isBookmarked?: boolean;
  onBookmarkToggle?: (poiId: string) => void;
  accessibleMode?: boolean;
}

export function POIListItem({
  poi,
  distance,
  onClick,
  isBookmarked,
  onBookmarkToggle,
  accessibleMode,
}: POIListItemProps) {
  const { t, i18n } = useTranslation("pois");
  const lang = i18n.language;
  const translatedName = getTranslatedValue(
    poi.name,
    poi.nameTranslations,
    lang,
  );
  const translatedCategoryName = poi.category
    ? getTranslatedValue(poi.category.name, poi.category.nameTranslations, lang)
    : undefined;

  const iconEntry = poi.category?.icon
    ? findIconByName(poi.category.icon)
    : null;

  const sponsorColor =
    poi.sponsorRef?.sponsorTierColor ??
    (poi.sponsorTier ? LEGACY_SPONSOR_COLORS[poi.sponsorTier] : undefined) ??
    null;
  const hasSponsor = !!sponsorColor;

  const photo = poi.photos?.[0];
  const photoUrl = photo
    ? getCloudinaryImageUrl(photo.cloudinaryPublicId, {
        version: photo.cloudinaryVersion,
        width: 96,
        aspectRatio: "1:1",
        crop: "fill",
      })
    : null;

  const categoryColor = poi.category?.color ?? FALLBACK_CATEGORY_COLOR;
  const fallbackBg = lightenHex(categoryColor, 0.7);
  const fallbackIconColor = lightenHex(categoryColor, 0.45);

  const walkTime =
    distance !== undefined ? formatWalkTime(distance, accessibleMode, t) : null;

  return (
    <button
      type="button"
      className="w-full flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 text-left border-b"
      onClick={onClick}
      aria-label={t("viewDetails", { name: translatedName })}
    >
      {/* Thumbnail: photo if available, else pastel category-color fallback */}
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={photo?.altText ?? ""}
          loading="lazy"
          className="w-12 h-12 rounded-lg object-cover shrink-0"
        />
      ) : (
        <div
          className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: fallbackBg }}
        >
          <FontAwesomeIcon
            icon={iconEntry?.icon ?? faMapPin}
            className="w-6 h-6"
            style={{ color: fallbackIconColor }}
          />
        </div>
      )}

      {/* Name + subcategory row */}
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <div className="font-medium text-base truncate text-foreground">
          {translatedName}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {translatedCategoryName && (
            <span className="flex items-center gap-1 min-w-0">
              <FontAwesomeIcon
                icon={iconEntry?.icon ?? faMapPin}
                className="w-3.5 h-3.5 text-foreground/30 shrink-0"
              />
              <span className="truncate">{translatedCategoryName}</span>
            </span>
          )}
          {poi.status === "temp_closed" && (
            <span className="text-amber-600 font-medium shrink-0">
              {t("temporarilyClosed")}
            </span>
          )}
          {walkTime && (
            <span className="flex items-center gap-1 shrink-0">
              <FontAwesomeIcon
                icon={faPersonWalking}
                className="w-3.5 h-3.5 text-foreground/30"
              />
              <span>{walkTime}</span>
            </span>
          )}
          {hasSponsor && (
            <FontAwesomeIcon
              icon={faStar}
              className="w-3.5 h-3.5 shrink-0"
              style={{ color: sponsorColor ?? undefined }}
              aria-label={t("sponsored")}
            />
          )}
        </div>
      </div>

      {/* Heart — bare icon, 44x44 touch target */}
      {onBookmarkToggle && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onBookmarkToggle(poi.id);
          }}
          className="min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0 active:scale-90 transition-transform -mr-2"
          aria-label={
            isBookmarked
              ? t("removeSaved", { name: translatedName })
              : t("savePoi", { name: translatedName })
          }
          aria-pressed={isBookmarked}
        >
          <FontAwesomeIcon
            icon={isBookmarked ? faHeart : faHeartRegular}
            className={cn(
              "w-5 h-5",
              isBookmarked ? "text-red-500" : "text-foreground/20",
            )}
          />
        </button>
      )}
    </button>
  );
}
